import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Tag,
  Send,
  MessageSquare,
  FileText,
  Zap,
  BarChart3,
  Settings,
  LogOut,
  ChevronDown,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-nav-dashboard" },
  { to: "/contacts", label: "Contacts", icon: Users, testId: "sidebar-nav-contacts" },
  { to: "/tags", label: "Tags", icon: Tag, testId: "sidebar-nav-tags" },
  { to: "/broadcasts", label: "Broadcasts", icon: Send, testId: "sidebar-nav-broadcasts" },
  { to: "/inbox", label: "Inbox", icon: MessageSquare, testId: "sidebar-nav-inbox" },
  { to: "/templates", label: "Templates", icon: FileText, testId: "sidebar-nav-templates" },
  { to: "/flows", label: "Flows", icon: Zap, testId: "sidebar-nav-flows" },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "sidebar-nav-reports" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "sidebar-nav-settings" },
];

const STORAGE_KEY = "flowsend.sidebar.collapsed";

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);
  return [collapsed, setCollapsed];
}

export default function Sidebar({ collapsed = false, onToggle, mobile = false, onNavigate }) {
  const { user, organization, logout } = useAuth();
  const initials = (user?.name || user?.email || "U")[0].toUpperCase();
  const showLabels = !collapsed || mobile;

  return (
    <aside
      data-testid="app-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className={`shrink-0 h-screen border-r border-slate-200 bg-white flex flex-col transition-[width] duration-200 ease-out ${
        mobile ? "w-64" : collapsed ? "w-[72px]" : "w-64"
      }`}
    >
      {/* Brand header + toggle */}
      <div className={`border-b border-slate-200 ${collapsed && !mobile ? "px-2 py-3" : "px-5 py-5"}`}>
        <div className={`flex items-center ${collapsed && !mobile ? "justify-center" : "justify-between"} gap-2`}>
          <div className={`flex items-center ${collapsed && !mobile ? "" : "gap-2"}`}>
            <div className="w-8 h-8 rounded-md bg-brand grid place-items-center text-white shrink-0">
              <Send className="w-4 h-4" />
            </div>
            {showLabels && (
              <div className="min-w-0">
                <div className="font-heading font-semibold text-slate-900 leading-tight">FlowSend</div>
                <div className="text-[11px] text-slate-500 truncate max-w-[140px]">
                  {organization?.name || "Workspace"}
                </div>
              </div>
            )}
          </div>
          {!mobile && onToggle && (
            <button
              type="button"
              onClick={onToggle}
              data-testid="sidebar-toggle"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className={`text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md p-1.5 transition-colors ${
                collapsed ? "absolute right-[-12px] top-5 bg-white border border-slate-200 shadow-sm" : ""
              }`}
            >
              {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className={`flex-1 ${collapsed && !mobile ? "px-2 py-3" : "px-3 py-4"} space-y-1 overflow-y-auto overflow-x-hidden`}>
        <TooltipProvider delayDuration={120}>
          {NAV.map((item) => {
            const Icon = item.icon;
            const link = (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center ${collapsed && !mobile ? "justify-center" : "gap-3"} px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-brand-light text-brand-dark"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
                {showLabels && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
            if (collapsed && !mobile) {
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>{item.label}</TooltipContent>
                </Tooltip>
              );
            }
            return link;
          })}
        </TooltipProvider>
      </nav>

      {/* User menu */}
      <div className={`border-t border-slate-200 ${collapsed && !mobile ? "p-2" : "p-3"}`}>
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="user-menu-trigger"
            className={`w-full flex items-center ${collapsed && !mobile ? "justify-center p-1" : "gap-2 p-2"} rounded-md hover:bg-slate-50 transition-colors`}
          >
            <Avatar className="w-8 h-8 shrink-0">
              <AvatarFallback className="bg-brand text-white text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            {showLabels && (
              <>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {user?.name || "User"}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem disabled className="text-xs text-slate-500">
              {organization?.name || "No organization"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="user-menu-logout"
              onClick={logout}
              className="text-red-600 focus:text-red-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
